import * as React from 'react';
import { connect } from 'react-redux';
import { expandCollapseManager } from '@wix/chat-web';
import wix from '../../services/wix';
import {
  ViewerOnWidgetCollapsed,
  ViewerOnWidgetExpand,
} from '../../constants/viewer-event-types';
import {
  focusChannel,
  getChannel,
  getChatSettings,
  getChatState,
  sendMessage,
  startChannel,
  updateChatOnPage,
  updateEditorSettingsDesign,
  updateHasMessages,
  updateVisibilitySettings,
} from '../../actions';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { wixAnalytics } from '../../services/wixAnalytics';
import { chatEventEmitter } from '@wix/chat-sdk';
import { onVisitorMerge } from '../../actions/visitorMerge';
import { loadingScheduler } from '../../services/loading-scheduler';

interface RootProps extends InjectedExperimentsProps {
  getChatState(data): any;
  getChatSettings(data): any;
  sendMessage(data): any;
  focusChannel(data): any;
  getChannel(data): any;
  startChannel(data): any;
  updateEditorSettingsDesign(design): any;
  onVisitorMerge(event): any;
  updateHasMessages(flag: boolean): any;
  updatePublicVisibility(flag: boolean): any;
  setChatOnPage(chatOnPage: boolean): any;
  editorSettingsDesign: any;
  editorSettingsBehaviour: any;
  hasMessages: boolean;
  publicVisibility: boolean;
  chatOnPage: boolean;
}

interface RootState {}

class _Root extends React.Component<RootProps, RootState> {
  handleExpandWidgetRequest(options: any = {}) {
    const requestId = options.data ? options.data.requestId : undefined;

    expandCollapseManager.registerOnExpand(() => {
      if (requestId) {
        const expandWidgetResponseEvent = `ChatWidget.expandWidgetResponse.${requestId}`;
        wix.publishToPubSub(expandWidgetResponseEvent, { success: true });
      }
    });

    expandCollapseManager.expand();
  }

  handleCollapseWidgetRequest(options: any = {}) {
    const requestId = options.data ? options.data.requestId : undefined;

    expandCollapseManager.registerOnCollapse(() => {
      const collapseWidgetResponseEvent = `ChatWidget.collapseWidgetResponse.${requestId}`;
      wix.publishToPubSub(collapseWidgetResponseEvent, { success: true });
    });

    expandCollapseManager.collapse();
  }

  handleSendMessageRequest = ({ data }) => {
    this.props.sendMessage(data);
  };

  handleGetChatStateRequest = ({ data }) => {
    this.props.getChatState(data);
  };

  handleGetChatSettingsRequest = ({ data }) => {
    this.props.getChatSettings(data);
  };

  handleFocusChannelRequest = ({ data }) => {
    this.props.focusChannel(data);
  };

  handleGetChannelRequest = ({ data }) => {
    this.props.getChannel(data);
  };

  handleStartChannelRequest = ({ data }) => {
    this.props.startChannel(data);
  };

  trackExpand = () => {
    wixAnalytics.expand();
  };

  trackCollapse = (data) => {
    if (!data || data.referrer !== 'page-visibility') {
      wixAnalytics.collapse();
    }
  };

  registerTrackEvents() {
    expandCollapseManager.registerOnExpand(this.trackExpand);
    expandCollapseManager.registerOnCollapse(this.trackCollapse);
  }

  unRegisterTrackEvents() {
    expandCollapseManager.unregisterOnExpand(this.trackExpand);
    expandCollapseManager.unregisterOnCollapse(this.trackCollapse);
  }

  componentDidMount() {
    wix.subscribeToExpandWidgetRequest(this.handleExpandWidgetRequest);
    wix.subscribeToCollapseWidgetRequest(this.handleCollapseWidgetRequest);

    wix.subscribeToGetChatStateRequest(this.handleGetChatStateRequest);
    wix.subscribeToGetChatSettingsRequest(this.handleGetChatSettingsRequest);

    wix.subscribeToSendMessageRequest(this.handleSendMessageRequest);

    wix.subscribeToFocusChannelRequest(this.handleFocusChannelRequest);

    wix.subscribeToGetChannelRequest(this.handleGetChannelRequest);

    wix.subscribeToStartChannelRequest(this.handleStartChannelRequest);

    this.registerTrackEvents();

    expandCollapseManager.registerOnExpand(() => {
      wix.publishToPubSub(ViewerOnWidgetExpand, {});
    });
    expandCollapseManager.registerOnCollapse(() => {
      wix.publishToPubSub(ViewerOnWidgetCollapsed, {});
    });

    this.props.updateEditorSettingsDesign(this.props.editorSettingsDesign);

    chatEventEmitter.addListener(
      chatEventEmitter.CHAT_EVENTS.EXTERNAL_HOST,
      this.handleVisitorMerge,
    );

    chatEventEmitter.addListener(
      chatEventEmitter.CHAT_EVENTS.NEW_REALTIME_MESSAGE,
      this.onNewMessage,
    );

    if (this.props.publicVisibility) {
      wix.getCurrentPageId((pageId) => this.handlePageVisibility(pageId));
      wix.subscribeToPageNavigation((page) =>
        this.handlePageVisibility(page.toPage),
      );
    }
  }

  handlePageVisibility = async (pageId: string) => {
    const {
      hasMessages,
      chatOnPage,
      setChatOnPage,
      editorSettingsBehaviour: { pagesWithChat },
    } = this.props;

    if (hasMessages) {
      return;
    }

    const showOnAllPages = pagesWithChat.includes('*');
    const showOnThisPage = pagesWithChat.includes(pageId);

    if (showOnAllPages || showOnThisPage) {
      if (!chatOnPage) {
        setChatOnPage(true);
        expandCollapseManager.collapse({ referrer: 'page-visibility' });
      }
    } else if (chatOnPage) {
      setChatOnPage(false);
      expandCollapseManager.collapse({ referrer: 'page-visibility' });
      void wix.resizeToHidden();
    }
  };

  handleVisitorMerge = (event) => {
    if (event.subType === 'VisitorMerge') {
      this.props.onVisitorMerge(event);
    }
  };

  onNewMessage = () => {
    void loadingScheduler.triggerInteractivity();
    this.props.updateHasMessages(true);
    this.props.updatePublicVisibility(true);
    this.props.setChatOnPage(true);
  };

  componentWillUnmount() {
    this.unRegisterTrackEvents();

    chatEventEmitter.removeListener(
      chatEventEmitter.CHAT_EVENTS.EXTERNAL_HOST,
      this.handleVisitorMerge,
    );

    chatEventEmitter.removeListener(
      chatEventEmitter.CHAT_EVENTS.NEW_REALTIME_MESSAGE,
      this.onNewMessage,
    );
  }

  render() {
    const { children } = this.props;
    return children;
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    getChatState: (data) => dispatch(getChatState(data)),
    getChatSettings: (data) => dispatch(getChatSettings(data)),
    sendMessage: (data) => dispatch(sendMessage(data)),
    focusChannel: (data) => dispatch(focusChannel(data)),
    getChannel: (data) => dispatch(getChannel(data)),
    startChannel: (data) => dispatch(startChannel(data)),
    updateEditorSettingsDesign: (design) =>
      dispatch(updateEditorSettingsDesign(design)),
    onVisitorMerge: (event) => dispatch(onVisitorMerge(event)),
    updateHasMessages: (flag) => dispatch(updateHasMessages(flag)),
    updatePublicVisibility: (flag) =>
      dispatch(updateVisibilitySettings({ publicVisibility: flag })),
    setChatOnPage: (chatOnPage) => dispatch(updateChatOnPage(chatOnPage)),
  };
};

function mapStateToProps(state) {
  return {
    editorSettingsDesign: state.editorSettingsDesign,
    editorSettingsBehaviour: state.editorSettingsBehaviour,
    hasMessages: state.hasMessages,
    publicVisibility: state.visibilitySettings.publicVisibility,
    chatOnPage: state.chatOnPage,
  };
}

export const Root = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withExperiments(_Root));
